import type {
  ContentBundleEnvelope,
  DisasterModeFixtureBundlePayload,
  SupportedLocale,
} from "@safesphere/contracts";
import { assembleReleaseWorkspace } from "@safesphere/release";
import type { ReleaseIndex, ReleaseIndexCandidate, ReleaseManifest } from "@safesphere/release";

import { canonicalJson } from "../offline/canonicalJson.js";
import {
  activateVerifiedBundle,
  appendUpdateAudit,
  getBundleStorageSnapshot,
  getStagedUpdate,
  listStagedUpdates,
  putStagedUpdate,
  restoreKnownGoodBundle,
} from "../offline/database.js";
import type { StagedUpdate, UpdateAuditEvent } from "../offline/database.js";
import { bytesToBase64Url } from "../offline/encoding.js";
import { cacheVerifiedBundleForOfflineUse } from "../offline/serviceWorkerBridge.js";
import { applicationVersion, verifyContentBundle } from "../offline/verifyBundle.js";

export type UpdateFailureReason =
  | "INDEX_UNAVAILABLE"
  | "INVALID_INDEX"
  | "NO_UPDATE"
  | "DOWNLOAD_INTERRUPTED"
  | "DOWNLOAD_FAILED"
  | "INVALID_MANIFEST"
  | "MANIFEST_HASH_MISMATCH"
  | "MANIFEST_BUNDLE_MISMATCH"
  | "BUNDLE_VERIFICATION_FAILED"
  | "PUBLICATION_INELIGIBLE"
  | "CONFIRMATION_REQUIRED"
  | "STAGED_UPDATE_MISSING"
  | "ROLLBACK_UNAVAILABLE";

export type UpdateDiscoveryResult =
  | { readonly ok: true; readonly status: "UP_TO_DATE" }
  | { readonly ok: true; readonly status: "UPDATE_AVAILABLE"; readonly update: StagedUpdate }
  | { readonly ok: false; readonly reason: UpdateFailureReason };

export type UpdateActionResult =
  | { readonly ok: true; readonly update: StagedUpdate }
  | { readonly ok: false; readonly reason: UpdateFailureReason };

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

let auditSequence = 0;

function nowIso(now: number): string {
  return new Date(now).toISOString();
}

async function audit(
  locale: SupportedLocale,
  type: UpdateAuditEvent["type"],
  detail: string,
  now: number,
  bundleId?: string,
): Promise<void> {
  auditSequence += 1;
  const event: UpdateAuditEvent = {
    id: `${nowIso(now)}:${String(auditSequence)}`,
    occurredAt: nowIso(now),
    locale,
    type,
    detail,
    ...(bundleId === undefined ? {} : { bundleId }),
  };
  await appendUpdateAudit(event);
}

function isReleaseIndex(value: unknown): value is ReleaseIndex {
  if (typeof value !== "object" || value === null) return false;
  const index = value as Partial<ReleaseIndex>;
  return (
    index.format === "SAFESPHERE_FICTIONAL_RELEASE_INDEX" &&
    index.version === 1 &&
    index.fictionalOnly === true &&
    typeof index.generatedAtUtc === "string" &&
    Array.isArray(index.releases) &&
    index.releases.every(isCandidate)
  );
}

function isCandidate(value: unknown): value is ReleaseIndexCandidate {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<ReleaseIndexCandidate>;
  return (
    typeof candidate.bundleId === "string" &&
    typeof candidate.contentVersion === "string" &&
    (candidate.locale === "th" || candidate.locale === "en") &&
    typeof candidate.createdAtUtc === "string" &&
    typeof candidate.bundleUrl === "string" &&
    typeof candidate.manifestUrl === "string" &&
    typeof candidate.manifestHash === "string" &&
    typeof candidate.signingKeyId === "string" &&
    typeof candidate.schemaVersion === "number" &&
    typeof candidate.minimumAppVersion === "string"
  );
}

function isManifest(value: unknown): value is ReleaseManifest {
  if (typeof value !== "object" || value === null) return false;
  const manifest = value as Partial<ReleaseManifest>;
  return (
    typeof manifest.bundleId === "string" &&
    (manifest.locale === "th" || manifest.locale === "en") &&
    typeof manifest.schemaVersion === "number" &&
    typeof manifest.createdAtUtc === "string" &&
    Array.isArray(manifest.revisionIds) &&
    Array.isArray(manifest.contentHashes) &&
    Array.isArray(manifest.governanceSnapshots) &&
    Array.isArray(manifest.publicationDecisions) &&
    typeof manifest.compatibility === "object"
  );
}

async function hashValue(value: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalJson(value));
  return bytesToBase64Url(new Uint8Array(await crypto.subtle.digest("SHA-256", bytes)));
}

function resolveArtifactUrl(indexUrl: string, artifactUrl: string): string {
  return new URL(artifactUrl, indexUrl).toString();
}

export async function discoverUpdate(
  locale: SupportedLocale,
  indexUrl: string,
  fetcher: FetchLike = fetch,
  now = Date.now(),
): Promise<UpdateDiscoveryResult> {
  let response: Response;
  try {
    response = await fetcher(indexUrl, { cache: "no-store" });
  } catch {
    await audit(locale, "DISCOVERY_FAILED", "release index request failed", now);
    return { ok: false, reason: "INDEX_UNAVAILABLE" };
  }
  if (!response.ok) {
    await audit(locale, "DISCOVERY_FAILED", `release index HTTP ${String(response.status)}`, now);
    return { ok: false, reason: "INDEX_UNAVAILABLE" };
  }
  let value: unknown;
  try {
    value = await response.json();
  } catch {
    return { ok: false, reason: "INVALID_INDEX" };
  }
  if (!isReleaseIndex(value)) return { ok: false, reason: "INVALID_INDEX" };
  const candidate = value.releases
    .filter((release) => release.locale === locale)
    .toSorted((left, right) => right.createdAtUtc.localeCompare(left.createdAtUtc))[0];
  if (candidate === undefined) return { ok: true, status: "UP_TO_DATE" };
  const active = (await getBundleStorageSnapshot(locale)).activeBundle;
  if (active?.bundle.body.contentVersion === candidate.contentVersion)
    return { ok: true, status: "UP_TO_DATE" };
  const update: StagedUpdate = {
    id: `${locale}:${candidate.bundleId}`,
    locale,
    status: "DISCOVERED",
    candidate,
    updatedAt: nowIso(now),
  };
  await putStagedUpdate(update);
  await audit(locale, "UPDATE_DISCOVERED", candidate.contentVersion, now, candidate.bundleId);
  return { ok: true, status: "UPDATE_AVAILABLE", update };
}

function manifestMatches(
  candidate: ReleaseIndexCandidate,
  manifest: ReleaseManifest,
  bundle: ContentBundleEnvelope,
): boolean {
  return (
    manifest.bundleId === candidate.bundleId &&
    manifest.bundleId === bundle.body.bundleId &&
    manifest.locale === candidate.locale &&
    bundle.body.locale === candidate.locale &&
    manifest.schemaVersion === candidate.schemaVersion &&
    manifest.schemaVersion === bundle.body.schemaVersion &&
    bundle.body.contentVersion === candidate.contentVersion &&
    bundle.signature.keyId === candidate.signingKeyId &&
    manifest.compatibility.minimumAppVersion === candidate.minimumAppVersion &&
    manifest.revisionIds.every((id) =>
      (bundle.body.payload as DisasterModeFixtureBundlePayload).records.some(
        (record) => record.revisionId === id,
      ),
    )
  );
}

export async function downloadAndVerifyUpdate(
  id: string,
  indexUrl: string,
  fetcher: FetchLike = fetch,
  now = Date.now(),
): Promise<UpdateActionResult> {
  const staged = await getStagedUpdate(id);
  if (staged === undefined) return { ok: false, reason: "STAGED_UPDATE_MISSING" };
  await putStagedUpdate({ ...staged, status: "DOWNLOADING", updatedAt: nowIso(now) });
  let manifestResponse: Response;
  let bundleResponse: Response;
  try {
    [manifestResponse, bundleResponse] = await Promise.all([
      fetcher(resolveArtifactUrl(indexUrl, staged.candidate.manifestUrl), { cache: "no-store" }),
      fetcher(resolveArtifactUrl(indexUrl, staged.candidate.bundleUrl), { cache: "no-store" }),
    ]);
  } catch {
    const failed = {
      ...staged,
      status: "FAILED" as const,
      failureReason: "DOWNLOAD_INTERRUPTED",
      updatedAt: nowIso(now),
    };
    await putStagedUpdate(failed);
    await audit(staged.locale, "DOWNLOAD_INTERRUPTED", "artifact request interrupted", now);
    return { ok: false, reason: "DOWNLOAD_INTERRUPTED" };
  }
  if (!manifestResponse.ok || !bundleResponse.ok) {
    await putStagedUpdate({
      ...staged,
      status: "FAILED",
      failureReason: "DOWNLOAD_FAILED",
      updatedAt: nowIso(now),
    });
    await audit(staged.locale, "DOWNLOAD_FAILED", "artifact request returned an error", now);
    return { ok: false, reason: "DOWNLOAD_FAILED" };
  }
  let manifest: unknown;
  let bundle: unknown;
  try {
    manifest = (await manifestResponse.json()) as unknown;
    bundle = (await bundleResponse.json()) as unknown;
  } catch {
    await audit(staged.locale, "DOWNLOAD_FAILED", "artifact JSON could not be parsed", now);
    return failVerification(staged, "DOWNLOAD_FAILED", now);
  }
  if (!isManifest(manifest)) return failVerification(staged, "INVALID_MANIFEST", now);
  if ((await hashValue(manifest)) !== staged.candidate.manifestHash)
    return failVerification(staged, "MANIFEST_HASH_MISMATCH", now);
  const downloaded: StagedUpdate = {
    ...staged,
    status: "VERIFYING",
    manifest,
    bundle: bundle as ContentBundleEnvelope,
    updatedAt: nowIso(now),
  };
  await putStagedUpdate(downloaded);
  const verification = await verifyContentBundle(bundle);
  if (!verification.ok) return failVerification(downloaded, "BUNDLE_VERIFICATION_FAILED", now);
  if (!manifestMatches(staged.candidate, manifest, verification.bundle))
    return failVerification(downloaded, "MANIFEST_BUNDLE_MISMATCH", now);
  const body = verification.bundle
    .body as ContentBundleEnvelope<DisasterModeFixtureBundlePayload>["body"];
  const releaseValidation = assembleReleaseWorkspace({
    locale: staged.locale,
    body,
    applicationVersion,
    createdAtUtc: staged.candidate.createdAtUtc,
  });
  if (!releaseValidation.complete)
    return failVerification(downloaded, "PUBLICATION_INELIGIBLE", now);
  const verified: StagedUpdate = { ...downloaded, status: "VERIFIED", updatedAt: nowIso(now) };
  await putStagedUpdate(verified);
  await audit(staged.locale, "UPDATE_VERIFIED", "all validation checks passed", now, body.bundleId);
  return { ok: true, update: verified };
}

async function failVerification(
  staged: StagedUpdate,
  reason: UpdateFailureReason,
  now: number,
): Promise<UpdateActionResult> {
  await putStagedUpdate({
    ...staged,
    status: "FAILED",
    failureReason: reason,
    updatedAt: nowIso(now),
  });
  await audit(staged.locale, "VERIFICATION_FAILED", reason, now, staged.candidate.bundleId);
  return { ok: false, reason };
}

export async function activateStagedUpdate(
  id: string,
  confirmed: boolean,
  now = Date.now(),
): Promise<UpdateActionResult> {
  if (!confirmed) return { ok: false, reason: "CONFIRMATION_REQUIRED" };
  const staged = await getStagedUpdate(id);
  if (staged?.status !== "VERIFIED" || staged.bundle === undefined)
    return { ok: false, reason: "STAGED_UPDATE_MISSING" };
  await activateVerifiedBundle({ bundle: staged.bundle, verifiedAt: nowIso(now) });
  await cacheVerifiedBundleForOfflineUse(staged.bundle);
  const activated: StagedUpdate = { ...staged, status: "ACTIVATED", updatedAt: nowIso(now) };
  await putStagedUpdate(activated);
  await audit(
    staged.locale,
    "ACTIVATED",
    "verified update activated",
    now,
    staged.bundle.body.bundleId,
  );
  window.dispatchEvent(new CustomEvent("safesphere:content-state-changed"));
  return { ok: true, update: activated };
}

export async function rollbackUpdate(
  locale: SupportedLocale,
  confirmed: boolean,
  now = Date.now(),
): Promise<UpdateActionResult> {
  if (!confirmed) return { ok: false, reason: "CONFIRMATION_REQUIRED" };
  const restored = await restoreKnownGoodBundle(locale);
  if (restored === undefined) return { ok: false, reason: "ROLLBACK_UNAVAILABLE" };
  await cacheVerifiedBundleForOfflineUse(restored.bundle);
  await audit(
    locale,
    "ROLLBACK",
    "retained verified bundle restored",
    now,
    restored.bundle.body.bundleId,
  );
  window.dispatchEvent(new CustomEvent("safesphere:content-state-changed"));
  const staged: StagedUpdate = {
    id: `${locale}:rollback:${restored.bundle.body.bundleId}`,
    locale,
    status: "ACTIVATED",
    candidate: {
      bundleId: restored.bundle.body.bundleId,
      contentVersion: restored.bundle.body.contentVersion,
      locale,
      createdAtUtc: restored.bundle.body.publishedAt,
      bundleUrl: "local-known-good",
      manifestUrl: "local-known-good",
      manifestHash: "local-known-good",
      signingKeyId: restored.bundle.signature.keyId,
      schemaVersion: restored.bundle.body.schemaVersion,
      minimumAppVersion: restored.bundle.body.compatibility.minimumAppVersion,
    },
    bundle: restored.bundle,
    updatedAt: nowIso(now),
  };
  return { ok: true, update: staged };
}

export async function recoverInterruptedUpdates(now = Date.now()): Promise<number> {
  const interrupted = (await listStagedUpdates()).filter(
    (update) => update.status === "DOWNLOADING" || update.status === "VERIFYING",
  );
  for (const update of interrupted) {
    await putStagedUpdate({
      ...update,
      status: "FAILED",
      failureReason: "DOWNLOAD_INTERRUPTED",
      updatedAt: nowIso(now),
    });
    await audit(update.locale, "DOWNLOAD_INTERRUPTED", "recovered after restart", now);
  }
  return interrupted.length;
}
